// SEO domains: search a name, buy it in Cloudflare Registrar, and put a
// public PWA (a plain PWA or a tracker campaign's) on its root for SEO.
// Super admin only — every purchase is charged and cannot be refunded.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import {
  getSeoDomains, searchSeoDomains, getSeoTargets, buySeoDomain, retrySeoDomain, updateSeoDomain, deleteSeoDomain,
  type SeoDomain, type SeoDomainState, type SeoQuote, type SeoTargetKind, type SeoDomainsListing,
} from '@/api/seo-domains';

const STATE_LABELS: Record<SeoDomainState, string> = {
  registering: 'Registering with Cloudflare…',
  registered: 'Registered — attaching…',
  zone: 'Registered — attaching…',
  attached: 'Waiting for the HTTPS certificate…',
  ready: 'Ready',
  failed: 'Registration failed',
};

const TITLE_MAX = 70;
const DESCRIPTION_MAX = 300;

function money(amount: string | null, currency: string | null): string {
  if (!amount) return '';
  return currency && currency !== 'USD' ? `${amount} ${currency}` : `$${amount}`;
}

function reasonLabel(q: SeoQuote): string {
  if (q.premium) return 'Premium';
  if (q.reason === 'extension_not_supported_via_api') return 'Not sold via API';
  return 'Unavailable';
}

export function SeoDomains() {
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const [buying, setBuying] = useState<SeoQuote | null>(null);
  const [editing, setEditing] = useState<SeoDomain | null>(null);

  const list = useQuery({
    queryKey: ['seo-domains'],
    queryFn: getSeoDomains,
    // The cron walks registration → attach → certificate minute by minute.
    refetchInterval: (q) => q.state.data?.domains.some((d) => d.state !== 'ready' && d.state !== 'failed' && !d.error) ? 15_000 : false,
  });
  const onListing = (data: SeoDomainsListing) => qc.setQueryData(['seo-domains'], data);

  const search = useMutation({ mutationFn: (q: string) => searchSeoDomains(q) });
  const buy = useMutation({
    mutationFn: (name: string) => buySeoDomain(name),
    onSuccess: (data) => { onListing(data); setBuying(null); search.reset(); setQuery(''); },
  });
  const retry = useMutation({ mutationFn: retrySeoDomain, onSuccess: onListing, onError: (e: Error) => alert(e.message) });
  const remove = useMutation({ mutationFn: deleteSeoDomain, onSuccess: onListing, onError: (e: Error) => alert(e.message) });

  const unavailable = list.data?.unavailable;
  const domains = list.data?.domains ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">SEO Domains</h1>
        <p className="text-sm text-content-subtle mt-1">
          Buy a domain in Cloudflare and put a public PWA on its root, with robots.txt, a sitemap and SEO tags.
        </p>
      </div>

      {unavailable && (
        <div className="p-3 bg-warning-soft text-warning text-sm rounded">Domain purchase is off: {unavailable}.</div>
      )}

      <section className="bg-surface border border-border rounded-lg p-4 space-y-4">
        <form
          className="flex flex-col sm:flex-row gap-2"
          onSubmit={(e) => { e.preventDefault(); if (query.trim()) search.mutate(query.trim()); }}
        >
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="total-casino"
            className="flex-1 px-3 py-2 border border-border-strong rounded-md text-sm bg-surface"
          />
          <Button type="submit" loading={search.isPending} disabled={!!unavailable || !query.trim()}>Search</Button>
        </form>
        {search.error && <p className="text-danger text-sm">{(search.error as Error).message}</p>}
        {search.data && (
          <div>
            <p className="text-xs text-content-subtle mb-2">
              {search.data.filter((q) => q.available).length} of {search.data.length} available
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {search.data.map((q) => (
                <button
                  key={q.name}
                  type="button"
                  disabled={!q.available}
                  onClick={() => { buy.reset(); setBuying(q); }}
                  className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md border text-left ${
                    q.available
                      ? 'border-border-strong hover:bg-surface-muted'
                      : 'border-border opacity-50 cursor-not-allowed'
                  }`}
                >
                  <span className="text-sm text-content truncate">{q.name}</span>
                  {q.available ? (
                    <span className="text-right shrink-0">
                      <span className="block text-sm font-medium text-content">{money(q.price, q.currency)}</span>
                      <span className="block text-xs text-content-subtle">Renews at {money(q.renewal_price, q.currency)}/year</span>
                    </span>
                  ) : (
                    <span className="text-xs text-content-subtle shrink-0">{reasonLabel(q)}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="font-medium text-content">Your domains</h2>
        {list.isLoading ? (
          <p className="text-sm text-content-subtle">Loading…</p>
        ) : domains.length === 0 ? (
          <p className="text-sm text-content-subtle">No SEO domains yet.</p>
        ) : domains.map((d) => (
          <div key={d.id} className="bg-surface border border-border rounded-lg p-4 space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 min-w-0">
                <a href={`https://${d.hostname}/`} target="_blank" rel="noreferrer" className="font-mono text-sm text-primary hover:underline break-all">
                  {d.hostname}
                </a>
                <span className={`text-xs ${d.state === 'ready' ? 'text-success' : d.state === 'failed' ? 'text-danger' : 'text-content-muted'}`}>
                  {d.error && d.state !== 'failed' ? 'Stopped' : STATE_LABELS[d.state]}
                </span>
              </div>
              <div className="flex gap-2">
                {d.state !== 'failed' && (
                  <Button size="sm" variant="secondary" onClick={() => setEditing(d)}>Edit</Button>
                )}
                {d.error && d.state !== 'failed' && (
                  <Button size="sm" variant="secondary" loading={retry.isPending && retry.variables === d.id} onClick={() => retry.mutate(d.id)}>Retry</Button>
                )}
                {d.state === 'failed' && (
                  <Button size="sm" variant="secondary" onClick={() => { if (confirm(`Remove ${d.hostname}? Nothing was bought.`)) remove.mutate(d.id); }}>Remove</Button>
                )}
              </div>
            </div>
            <p className="text-xs text-content-subtle">
              {[
                d.price && `${money(d.price, d.currency)} first year`,
                d.renewal_price && `renews at ${money(d.renewal_price, d.currency)}`,
                d.expires_at && `expires ${d.expires_at.slice(0, 10)}`,
                d.auto_renew === 1 ? 'auto-renew on' : d.auto_renew === 0 ? 'auto-renew off' : null,
              ].filter(Boolean).join(' · ')}
            </p>
            <p className="text-sm text-content-muted">
              {d.target_kind
                ? <>Root shows {d.target_kind === 'pwa' ? 'PWA' : 'tracker campaign'} <span className="text-content">{d.target_name ?? d.target_id}</span>{d.target_slug ? <span className="text-content-subtle"> (/{d.target_slug})</span> : null}</>
                : 'Nothing published on the root yet.'}
            </p>
            {d.seo_title && <p className="text-xs text-content-subtle">Title: {d.seo_title}</p>}
            {d.error && <p className="text-danger text-sm">{d.error}</p>}
          </div>
        ))}
      </section>

      <Modal isOpen={!!buying} onClose={() => setBuying(null)} title="Buy domain" size="md">
        {buying && (
          <div className="space-y-4 text-sm">
            <p>
              Buy <span className="font-mono font-medium">{buying.name}</span> for{' '}
              <strong>{money(buying.price, buying.currency)}</strong> the first year, renewing at{' '}
              {money(buying.renewal_price, buying.currency)}/year (auto-renew on)?
            </p>
            <p className="text-content-subtle">The purchase is charged to the Cloudflare account and cannot be refunded.</p>
            {buy.error && <p className="text-danger">{(buy.error as Error).message}</p>}
            <div className="flex justify-end gap-3 pt-3 border-t border-border">
              <Button variant="secondary" onClick={() => setBuying(null)}>Cancel</Button>
              <Button onClick={() => buy.mutate(buying.name)} loading={buy.isPending}>Buy for {money(buying.price, buying.currency)}</Button>
            </div>
          </div>
        )}
      </Modal>

      {editing && <EditModal domain={editing} onClose={() => setEditing(null)} onSaved={(data) => { onListing(data); setEditing(null); }} />}
    </div>
  );
}

function EditModal({ domain, onClose, onSaved }: {
  domain: SeoDomain; onClose: () => void; onSaved: (data: SeoDomainsListing) => void;
}) {
  const targets = useQuery({ queryKey: ['seo-domain-targets'], queryFn: getSeoTargets });
  const [kind, setKind] = useState<SeoTargetKind | ''>(domain.target_kind ?? '');
  const [targetId, setTargetId] = useState(domain.target_id ?? '');
  const [title, setTitle] = useState(domain.seo_title ?? '');
  const [description, setDescription] = useState(domain.seo_description ?? '');
  const attached = domain.state === 'attached' || domain.state === 'ready';

  const save = useMutation({
    mutationFn: () => updateSeoDomain(domain.id, {
      target_kind: kind || null, target_id: kind ? targetId || null : null,
      seo_title: title, seo_description: description,
    }),
    onSuccess: onSaved,
  });

  const campaigns = targets.data?.campaigns ?? [];
  // The domain's own copy shows up in the list too; the original sits elsewhere.
  const options = kind === 'pwa'
    ? (targets.data?.pwas ?? []).map((p) => ({ id: p.id, label: p.name }))
    : campaigns.map((c) => ({ id: c.id, label: `${c.name} — ${c.hostname}/${c.slug}` }));

  return (
    <Modal isOpen onClose={onClose} title={domain.hostname} size="lg">
      <div className="space-y-4 text-sm">
        <div>
          <label className="block font-medium mb-1">Root shows</label>
          <select
            value={kind}
            onChange={(e) => { setKind(e.target.value as SeoTargetKind | ''); setTargetId(''); }}
            className="block w-full px-3 py-2 border border-border-strong rounded-md bg-surface"
          >
            <option value="">Nothing</option>
            <option value="pwa">PWA</option>
            <option value="tracker" disabled={!attached}>Tracker campaign{attached ? '' : ' (once the domain is attached)'}</option>
          </select>
        </div>
        {kind && (
          <div>
            <label className="block font-medium mb-1">{kind === 'pwa' ? 'PWA' : 'Campaign'}</label>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="block w-full px-3 py-2 border border-border-strong rounded-md bg-surface"
            >
              <option value="">{targets.isLoading ? 'Loading…' : 'Choose…'}</option>
              {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            {kind === 'tracker' && (
              <p className="mt-1 text-xs text-content-subtle">
                A campaign from another domain is copied onto this one with its flows and offers; the original and its links stay as they are.
              </p>
            )}
          </div>
        )}
        <div>
          <label className="block font-medium mb-1">SEO title</label>
          <input
            type="text"
            value={title}
            maxLength={TITLE_MAX}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Leave empty to keep the PWA's own title"
            className="block w-full px-3 py-2 border border-border-strong rounded-md bg-surface"
          />
          <p className="mt-1 text-xs text-content-subtle text-right">{title.length}/{TITLE_MAX}</p>
        </div>
        <div>
          <label className="block font-medium mb-1">Meta description</label>
          <textarea
            value={description}
            maxLength={DESCRIPTION_MAX}
            rows={3}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Leave empty to use the PWA's short description"
            className="block w-full px-3 py-2 border border-border-strong rounded-md bg-surface"
          />
          <p className="mt-1 text-xs text-content-subtle text-right">{description.length}/{DESCRIPTION_MAX}</p>
        </div>
        {save.error && <p className="text-danger">{(save.error as Error).message}</p>}
        <div className="flex justify-end gap-3 pt-3 border-t border-border">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!!kind && !targetId}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}
